/**
 * usePaymentDocumentTypes
 *
 * Single source of truth for what each 憑證類型 (documentType) means to the
 * request form and every downstream payment surface:
 *   - which invoice fields are visible / required
 *   - what those fields are called for that document type
 *   - which fields must be filled before a line can be submitted
 *
 * Extracted from useReceiptHelpers.ts (which had grown past the 600-line
 * composable limit). Pure module-scope functions — no reactive dependencies,
 * no Vue imports — so both components and other composables can import
 * directly. useReceiptHelpers re-exports everything here for back-compat.
 */

// ── Document type field visibility ────────────────────────────────────────────

/**
 * 這張表在畫面上怎麼稱呼「那份紙本」。
 *
 * 一般請款單收的就是發票，所以預設 `發票`。出差旅費收的是收據、行程單、登機證，
 * 只有一部分是發票 —— 同一個畫面一下寫「發票」一下寫「憑證」，申請人會以為是兩件
 * 不同的東西，所以整張表改用上位詞「憑證」（PO decision，travel v2）。
 *
 * 只換「通稱」：`統一發票` / `收據` 是憑證的 TYPE 名稱，不隨這個開關改字。
 */
export type DocumentNoun = '發票' | '憑證';

export type DocumentDateLabel = '發票日期' | '憑證日期' | '收據日期' | '遺失日期' | '消費日期';
export type DocumentAmountLabel = '發票總額' | '憑證總額' | '收據金額' | '支出金額' | '金額';

/** 差旅單收的是憑證（收據／行程單／登機證），其他請款單是統一發票。單一定義，元件只讀。 */
export function documentNounFor(type: string | null | undefined): DocumentNoun {
  return type === 'travel_expense' ? '憑證' : '發票';
}

/** 通稱 → 該通稱底下的欄位標題。新增通稱時這裡是唯一要改的地方。 */
const NOUN_LABELS: Record<DocumentNoun, { date: DocumentDateLabel; amount: DocumentAmountLabel }> =
  {
    發票: { date: '發票日期', amount: '發票總額' },
    憑證: { date: '憑證日期', amount: '憑證總額' },
  };

export interface DocumentTypeFieldVisibility {
  invoiceNumber: boolean;
  invoiceDate: boolean;
  dateLabel: DocumentDateLabel;
  /**
   * What the amount column is called for this document type. 發票總額 is only
   * correct for 統一發票 — a 免憑證 line has no invoice and a 收據 line has no
   * 發票, so labelling their amount "發票總額" tells the requester to look for a
   * document that does not exist.
   */
  amountLabel: DocumentAmountLabel;
  companyTaxIdReminder: boolean;
  receiptUpload: boolean;
  /**
   * Whether the 後開發票 (deferred) checkbox applies. Only 統一發票 supports later
   * issuance — 收據 is point-of-sale, 憑證遺失 is already lost, 免憑證 has nothing.
   */
  canDefer: boolean;
}

/**
 * Returns which form fields are visible/relevant for a given documentType.
 * When invoiceDeferred is true, invoice fields and the receipt upload slot are all
 * hidden regardless of documentType (deferred override from plan Part 4).
 */
export function getDocumentTypeFieldVisibility(
  documentType: string | undefined,
  invoiceDeferred: boolean | undefined,
  /** 畫面上的通稱。預設 `發票`，所以既有呼叫端一個字都不會變。 */
  documentNoun: DocumentNoun = '發票',
): DocumentTypeFieldVisibility {
  const noun = NOUN_LABELS[documentNoun];
  if (invoiceDeferred === true) {
    return {
      invoiceNumber: false,
      invoiceDate: false,
      dateLabel: '消費日期',
      amountLabel: noun.amount, // still an invoice — just issued after payment
      companyTaxIdReminder: false,
      receiptUpload: false,
      canDefer: true, // already deferred — keep checkbox visible/checked on uniform_invoice
    };
  }
  switch (documentType) {
    case 'uniform_invoice':
      return {
        invoiceNumber: true,
        invoiceDate: true,
        dateLabel: noun.date,
        amountLabel: noun.amount,
        companyTaxIdReminder: true,
        receiptUpload: true,
        canDefer: true,
      };
    case 'receipt_only':
      return {
        invoiceNumber: false,
        invoiceDate: true,
        dateLabel: '收據日期',
        amountLabel: '收據金額',
        companyTaxIdReminder: false,
        receiptUpload: true,
        canDefer: false,
      };
    case 'no_document':
      return {
        invoiceNumber: false,
        invoiceDate: false,
        dateLabel: '消費日期',
        amountLabel: '支出金額',
        companyTaxIdReminder: false,
        receiptUpload: false,
        canDefer: false,
      };
    case 'document_elsewhere':
      // The document exists — on ANOTHER request. Nothing to collect or upload
      // HERE (the line stays out of the receipt cycle, like 免憑證), but the
      // 發票號碼 IS captured: it is the same number the paired request carries,
      // which makes the two halves matchable by sorting the CPA sheet on it.
      // Storing it is safe — lineHasClaimableInvoiceNumber only claims for
      // uniform_invoice, so the duplicate check never fires on this line.
      // No 發票日期: the paired request holds it, and the requester may not.
      return {
        invoiceNumber: false,
        invoiceDate: false,
        dateLabel: '消費日期',
        amountLabel: '支出金額',
        companyTaxIdReminder: false,
        receiptUpload: false,
        canDefer: false,
      };
    case 'document_lost':
      // Requester does not know the exact loss date, so no date input.
      // Reason for loss is entered in the request-level 備註 field.
      return {
        invoiceNumber: false,
        invoiceDate: false,
        dateLabel: '消費日期',
        amountLabel: '支出金額',
        companyTaxIdReminder: false,
        receiptUpload: false,
        canDefer: false,
      };
    default:
      return {
        invoiceNumber: false,
        invoiceDate: false,
        dateLabel: '消費日期',
        amountLabel: '金額',
        companyTaxIdReminder: false,
        receiptUpload: false,
        canDefer: false,
      };
  }
}

// ── Receipt-bearing predicate (SINGLE source of truth) ───────────────────────

/**
 * Whether a line participates in the receipt-collection cycle — i.e. a physical
 * document is expected against THIS request and 核收 must eventually happen.
 *
 * This rule was previously written inline in ~30 places, in two mirror-image
 * forms that are NOT equivalent when a new document type is introduced:
 *
 *   positive  documentType === 'uniform_invoice' || === 'receipt_only'
 *   negative  documentType !== 'no_document'     && !== 'document_lost'
 *
 * A grep for 'no_document' finds only the negative form, and the two disagree
 * about any type added later — the positive form excludes it, the negative form
 * includes it. That divergence is silent: no type error, no runtime error, just
 * different screens disagreeing about whether a line needs an invoice. Route
 * every call through here so a new type is decided in exactly one place.
 */
export function isReceiptBearingDocType(documentType: string | null | undefined): boolean {
  return documentType === 'uniform_invoice' || documentType === 'receipt_only';
}

/**
 * 看影像那顆的字：出差旅費一律「看憑證」；其餘依這一行的憑證類型 —— 收據「看收據」、
 * 統一發票「看發票」（RL §1：收據的行原本也寫「看發票」）。
 */
export function viewDocumentLabel(
  documentNoun: DocumentNoun,
  documentType: string | null | undefined,
): string {
  if (documentNoun === '憑證') return '看憑證';
  return documentType === 'receipt_only' ? '看收據' : '看發票';
}

/** Inverse of isReceiptBearingDocType — no document is expected on this request. */
export function isNoReceiptDocType(documentType: string | null | undefined): boolean {
  return !isReceiptBearingDocType(documentType);
}

// ── Document type tag colour ──────────────────────────────────────────────────

export type DocTypeTagType = 'info' | 'warning' | 'default' | 'error';

/**
 * Naive UI tag type per document type. Shared so every surface colours 統一發票 /
 * 收據 / 憑證遺證 the same way instead of re-deriving it locally.
 */
export function getDocTypeTagType(documentType: string | undefined): DocTypeTagType {
  switch (documentType) {
    case 'uniform_invoice':
      return 'info';
    case 'receipt_only':
      return 'warning';
    case 'document_lost':
      return 'error';
    default:
      return 'default';
  }
}

// ── Per-row required-field validation (single source of truth) ───────────────

/**
 * Field keys that may be empty/invalid on a single line item.
 * Used by both:
 *   - LineItemTable's `fieldStatus()` to render Naive UI `status="error"` per cell
 *   - RequestFormPage's `validateLineItems()` to build the user-facing toast
 *   - submitPaymentRequest's per-row gate (mirrored in TS on the backend)
 *
 * H-8: centralizing this prevents drift between the per-cell red-border logic
 * and the toast message — both must agree on what's required for what doc type.
 */
export type LineItemRequiredField =
  | 'description'
  | 'category'
  | 'amountWithTax'
  | 'expenseDate'
  | 'invoiceDate'
  | 'invoiceNumber'
  | 'itemNote';

/**
 * Returns the set of required fields that are missing/invalid on a line item,
 * given the documentType matrix and invoiceDeferred flag.
 *
 * Empty set ⇒ row is fully valid for submit.
 */
export function getLineItemMissingFields(item: {
  description?: string;
  category?: string;
  amountWithTax?: number;
  expenseDate?: string | null;
  invoiceDate?: string | null;
  invoiceNumber?: string;
  documentType?: string;
  invoiceDeferred?: boolean;
  itemNote?: string;
}): LineItemRequiredField[] {
  const missing: LineItemRequiredField[] = [];
  if (!item.description?.trim()) missing.push('description');
  if (!item.category) missing.push('category');
  if (
    typeof item.amountWithTax !== 'number' ||
    !Number.isInteger(item.amountWithTax) ||
    item.amountWithTax <= 0
  ) {
    missing.push('amountWithTax');
  }
  if (!item.expenseDate) missing.push('expenseDate');

  const vis = getDocumentTypeFieldVisibility(item.documentType, item.invoiceDeferred);
  // invoiceDate required whenever the visibility matrix says it's visible
  if (vis.invoiceDate && !item.invoiceDate) missing.push('invoiceDate');
  if (vis.invoiceNumber && !item.invoiceNumber?.trim()) missing.push('invoiceNumber');
  // 憑證另附 has NO 發票號碼 field at all. An invoice number that is deliberately
  // never duplicate-checked (the claim gate accepts uniform_invoice only) looks
  // authoritative but isn't — a trap for anyone reading the data later. The
  // required note carries the trace instead: it is prose, so nobody mistakes it
  // for a validated key, and it exists even when the invoice does not yet.
  if (item.documentType === 'document_elsewhere' && !item.itemNote?.trim()) {
    missing.push('itemNote');
  }
  return missing;
}

/** Display labels for the required fields, keyed by field + documentType context. */
export function getLineItemFieldLabel(
  field: LineItemRequiredField,
  documentType: string | undefined,
  invoiceDeferred: boolean | undefined,
  /**
   * 畫面上的通稱。預設 `發票`，所以既有呼叫端一個字都不會變；出差旅費傳 `憑證`，
   * 訊息裡的欄位名才會與那張表上印的欄位名一致（憑證日期／憑證號碼）。
   */
  documentNoun: DocumentNoun = '發票',
): string {
  switch (field) {
    case 'description':
      return '費用名稱';
    case 'category':
      return '費用類別';
    case 'amountWithTax':
      return getDocumentTypeFieldVisibility(documentType, invoiceDeferred, documentNoun)
        .amountLabel;
    case 'expenseDate':
      return '消費日期';
    case 'invoiceDate':
      return getDocumentTypeFieldVisibility(documentType, invoiceDeferred, documentNoun).dateLabel;
    case 'invoiceNumber':
      return `${documentNoun}號碼`;
    case 'itemNote':
      return '費用備註';
  }
}
